import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MdAdd, MdSearch, MdOutlineVerified, MdHourglassEmpty, MdMailOutline, MdArrowForward } from 'react-icons/md';
import './WelcomeTab.css';
import api from '../utils/api';
import { useGlobalState } from '../Provider/AppProvider';

interface Certificate {
    id: number;
    first_name: string;
    last_name: string;
    organization: string;
    course_name: string;
    issue_date: string;
    certificate_hash: string;
}

interface WaitingEntry {
    id: number;
    email: string;
    role: string;
    created_at: string;
}

interface ActivityItem {
    key: string;
    title: string;
    detail: string;
    date: Date;
    kind: 'certificate' | 'waiting';
}

const toList = (data: any): any[] => {
    if (Array.isArray(data)) return data;
    const raw = data && typeof data === 'object' ? (data.messages ?? data.data ?? data.items) : null;
    return Array.isArray(raw) ? raw : [];
};

const greeting = () => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
};

const timeAgo = (d: Date) => {
    const mins = Math.round((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours} h ago`;
    const days = Math.round(hours / 24);
    if (days < 30) return `${days} d ago`;
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const Highlight = ({ text, query }: { text: string; query: string }) => {
    const i = query ? text.toLowerCase().indexOf(query.toLowerCase()) : -1;
    if (i < 0) return <>{text}</>;
    return <>{text.slice(0, i)}<mark>{text.slice(i, i + query.length)}</mark>{text.slice(i + query.length)}</>;
};

const WelcomeTab: React.FC = () => {
    const { darkMode, switchView } = useGlobalState();
    const user = JSON.parse(localStorage.getItem('user') || '{"username": "Admin"}');

    const [certificates, setCertificates] = useState<Certificate[]>([]);
    const [waiting, setWaiting] = useState<WaitingEntry[]>([]);
    const [messageCount, setMessageCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [active, setActive] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        let cancelled = false;
        // Each request is independent so one failing endpoint does not blank the page.
        Promise.allSettled([
            api.get('/v1/vitrine/certificates/list'),
            api.get('/v1/vitrine/waiting-list'),
            api.get('/v1/vitrine/contact/messages'),
        ]).then(([certs, wait, msgs]) => {
            if (cancelled) return;
            if (certs.status === 'fulfilled') setCertificates(toList(certs.value.data));
            if (wait.status === 'fulfilled') setWaiting(toList(wait.value.data));
            if (msgs.status === 'fulfilled') setMessageCount(toList(msgs.value.data).length);
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, []);

    // Press "/" anywhere on Home to jump to the search box.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const tag = (e.target as HTMLElement)?.tagName;
            if (e.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA') {
                e.preventDefault();
                inputRef.current?.focus();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const q = query.trim();
    const results = useMemo(() => {
        if (!q) return [];
        const needle = q.toLowerCase();
        return certificates
            .filter((c) =>
                `${c.first_name} ${c.last_name}`.toLowerCase().includes(needle) ||
                c.certificate_hash.toLowerCase().includes(needle) ||
                (c.organization || '').toLowerCase().includes(needle) ||
                (c.course_name || '').toLowerCase().includes(needle))
            .slice(0, 6);
    }, [q, certificates]);

    const activity = useMemo<ActivityItem[]>(() => {
        const certItems: ActivityItem[] = certificates.map((c) => ({
            key: `c${c.id}`,
            title: `Certificate issued to ${c.first_name} ${c.last_name}`,
            detail: c.course_name,
            date: new Date(c.issue_date),
            kind: 'certificate',
        }));
        const waitItems: ActivityItem[] = waiting.map((w) => ({
            key: `w${w.id}`,
            title: `${w.email} joined the waiting list`,
            detail: w.role,
            date: new Date(w.created_at),
            kind: 'waiting',
        }));
        return [...certItems, ...waitItems]
            .filter((a) => !isNaN(a.date.getTime()))
            .sort((a, b) => b.date.getTime() - a.date.getTime())
            .slice(0, 6);
    }, [certificates, waiting]);

    const onSearchKey = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
        else if (e.key === 'Enter' && results.length) switchView('List Certificate');
        else if (e.key === 'Escape') { setQuery(''); inputRef.current?.blur(); }
    };

    const count = (n: number) => (loading ? <span className="skeleton" /> : n.toLocaleString());

    const tiles = [
        { view: 'Issue Certificate', label: 'Issue', value: <MdAdd />, hint: 'New certificate', tone: 'blue', Icon: MdAdd },
        { view: 'List Certificate', label: 'Certificates', value: count(certificates.length), hint: 'All issued', tone: 'green', Icon: MdOutlineVerified },
        { view: 'Waiting List', label: 'Waiting list', value: count(waiting.length), hint: 'Sign-ups', tone: 'amber', Icon: MdHourglassEmpty },
        { view: 'Messages', label: 'Messages', value: count(messageCount), hint: 'From contact form', tone: 'violet', Icon: MdMailOutline },
    ];

    return (
        <div className={`home ${darkMode ? 'dark' : ''}`}>
            <div className="home-inner">
                <header className="home-hero">
                    <p className="home-eyebrow">{greeting()}, {user.username}</p>
                    <h1 className="home-title">Find a certificate</h1>

                    <div className={`home-search${q ? ' open' : ''}`}>
                        <MdSearch className="home-search-icon" aria-hidden />
                        <input
                            ref={inputRef}
                            type="search"
                            value={query}
                            onChange={(e) => { setQuery(e.target.value); setActive(0); }}
                            onKeyDown={onSearchKey}
                            placeholder="Name, certificate hash, organization or course"
                            aria-label="Search certificates"
                            role="combobox"
                            aria-expanded={!!q}
                            aria-controls="home-results"
                        />
                        <kbd className="home-kbd" aria-hidden>/</kbd>

                        {q && (
                            <ul className="home-results" id="home-results" role="listbox">
                                {results.length === 0 && (
                                    <li className="home-empty">
                                        {loading ? 'Loading certificates…' : <>No certificate matches “{q}”.</>}
                                    </li>
                                )}
                                {results.map((c, i) => (
                                    <li
                                        key={c.id}
                                        role="option"
                                        aria-selected={i === active}
                                        className={i === active ? 'active' : ''}
                                        onMouseEnter={() => setActive(i)}
                                        onClick={() => switchView('List Certificate')}
                                    >
                                        <span className="home-result-avatar">{c.first_name?.[0]}{c.last_name?.[0]}</span>
                                        <span className="home-result-main">
                                            <strong><Highlight text={`${c.first_name} ${c.last_name}`} query={q} /></strong>
                                            <small><Highlight text={c.course_name || ''} query={q} /> · {c.organization}</small>
                                        </span>
                                        <code><Highlight text={c.certificate_hash.slice(0, 10)} query={q} /></code>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <p className="home-hint">Press <kbd>/</kbd> to search from anywhere on this page.</p>
                </header>

                <section className="home-tiles" aria-label="Shortcuts">
                    {tiles.map(({ view, label, value, hint, tone, Icon }) => (
                        <button key={view} className={`home-tile ${tone}`} onClick={() => switchView(view)}>
                            <span className="home-tile-icon"><Icon /></span>
                            <span className="home-tile-label">{label}</span>
                            <span className="home-tile-value">{value}</span>
                            <span className="home-tile-hint">{hint}<MdArrowForward aria-hidden /></span>
                        </button>
                    ))}
                </section>

                <section className="home-activity">
                    <h2>Recent activity</h2>
                    {loading && <div className="skeleton-lines"><span /><span /><span /></div>}
                    {!loading && activity.length === 0 && <p className="home-empty">Nothing yet. Issue your first certificate to get started.</p>}
                    {!loading && activity.length > 0 && (
                        <ol>
                            {activity.map((a) => (
                                <li key={a.key} className={a.kind}>
                                    <span className="home-dot" aria-hidden />
                                    <span className="home-activity-main">
                                        <strong>{a.title}</strong>
                                        <small>{a.detail}</small>
                                    </span>
                                    <time dateTime={a.date.toISOString()}>{timeAgo(a.date)}</time>
                                </li>
                            ))}
                        </ol>
                    )}
                </section>
            </div>
        </div>
    );
};

export default WelcomeTab;
